import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export type ThemeSetting = 'system' | 'dark' | 'light';
export type ResolvedTheme = 'dark' | 'light';
export type GlassSetting = 'auto' | 'on' | 'off';

export interface ThemeState {
  theme: ThemeSetting;
  resolvedTheme: ResolvedTheme;
  glass: GlassSetting;
  glassEnabled: boolean;
  setTheme: (t: ThemeSetting) => void;
  setGlass: (g: GlassSetting) => void;
}

const Ctx = createContext<ThemeState | null>(null);

const STORAGE_THEME = 'mos.theme';
const STORAGE_GLASS = 'mos.glass';

/** Heuristic: low-end devices get solid surfaces instead of backdrop blur. */
export function detectLowEndDevice(): boolean {
  if (typeof navigator === 'undefined') return false;
  const nav = navigator as Navigator & { deviceMemory?: number };
  if (typeof nav.deviceMemory === 'number' && nav.deviceMemory <= 3) return true;
  const android = /Android/i.test(navigator.userAgent);
  if (android && typeof navigator.hardwareConcurrency === 'number' && navigator.hardwareConcurrency <= 4) return true;
  if (typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-transparency: reduce)').matches) return true;
  return false;
}

function systemTheme(): ResolvedTheme {
  if (typeof matchMedia === 'undefined') return 'dark';
  return matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
}

function readStored<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  try {
    const v = localStorage.getItem(key) as T | null;
    return v && allowed.includes(v) ? v : fallback;
  } catch {
    return fallback;
  }
}

/** Applies data-theme / data-glass to <html>. Also usable at boot via `applyThemeAttributes()` in an inline script. */
export function applyThemeAttributes(theme: ThemeSetting, glass: GlassSetting) {
  if (typeof document === 'undefined') return;
  const resolved: ResolvedTheme = theme === 'system' ? systemTheme() : theme;
  const enabled = glass === 'on' ? true : glass === 'off' ? false : !detectLowEndDevice();
  const el = document.documentElement;
  el.dataset.theme = resolved;
  el.dataset.glass = enabled ? 'on' : 'off';
  const meta = document.querySelector('meta[name="theme-color"]:not([media])');
  if (meta) meta.setAttribute('content', resolved === 'dark' ? '#141416' : '#F2F2F7');
}

export function ThemeProvider({ children, defaultTheme = 'dark' }: { children: ReactNode; defaultTheme?: ThemeSetting }) {
  const [theme, setThemeState] = useState<ThemeSetting>(() => readStored(STORAGE_THEME, ['system', 'dark', 'light'] as const, defaultTheme));
  const [glass, setGlassState] = useState<GlassSetting>(() => readStored(STORAGE_GLASS, ['auto', 'on', 'off'] as const, 'auto'));
  const [sys, setSys] = useState<ResolvedTheme>(() => systemTheme());

  useEffect(() => {
    if (typeof matchMedia === 'undefined') return;
    const mq = matchMedia('(prefers-color-scheme: light)');
    const onChange = () => setSys(mq.matches ? 'light' : 'dark');
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    applyThemeAttributes(theme, glass);
  }, [theme, glass, sys]);

  const setTheme = useCallback((t: ThemeSetting) => {
    setThemeState(t);
    try {
      localStorage.setItem(STORAGE_THEME, t);
    } catch {
      /* ignore */
    }
  }, []);
  const setGlass = useCallback((g: GlassSetting) => {
    setGlassState(g);
    try {
      localStorage.setItem(STORAGE_GLASS, g);
    } catch {
      /* ignore */
    }
  }, []);

  const value = useMemo<ThemeState>(() => {
    const resolvedTheme = theme === 'system' ? sys : theme;
    const glassEnabled = glass === 'on' ? true : glass === 'off' ? false : !detectLowEndDevice();
    return { theme, resolvedTheme, glass, glassEnabled, setTheme, setGlass };
  }, [theme, glass, sys, setTheme, setGlass]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTheme(): ThemeState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTheme must be used within <ThemeProvider>');
  return v;
}

/** Inline boot script (string) to avoid a flash of wrong theme before React mounts. */
export const themeBootScript = `(function(){try{var t=localStorage.getItem('${STORAGE_THEME}')||'dark';var g=localStorage.getItem('${STORAGE_GLASS}')||'auto';var r=t==='system'?(matchMedia('(prefers-color-scheme: light)').matches?'light':'dark'):t;var low=(navigator.deviceMemory&&navigator.deviceMemory<=3)||(/Android/i.test(navigator.userAgent)&&navigator.hardwareConcurrency<=4)||matchMedia('(prefers-reduced-transparency: reduce)').matches;var e=g==='on'?true:g==='off'?false:!low;var d=document.documentElement;d.dataset.theme=r;d.dataset.glass=e?'on':'off';}catch(e){}})();`;
